import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Calendar, Video, Building2, Loader2 } from "lucide-react";

export default function DoctorAppointments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("upcoming");

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/appointments");
    setItems(data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const complete = async (id) => {
    try { await api.post(`/appointments/${id}/complete`); toast.success("Marked completed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const cancel = async (id) => {
    try { await api.post(`/appointments/${id}/cancel`); toast.success("Cancelled"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = items.filter((a) => {
    if (tab === "upcoming") return ["confirmed", "rescheduled", "pending"].includes(a.status);
    if (tab === "completed") return a.status === "completed";
    if (tab === "cancelled") return a.status === "cancelled";
    return true;
  });

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-slate-900">Appointments</h1>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-full bg-slate-100 p-1">
          <TabsTrigger value="upcoming" className="rounded-full">Upcoming</TabsTrigger>
          <TabsTrigger value="completed" className="rounded-full">Completed</TabsTrigger>
          <TabsTrigger value="cancelled" className="rounded-full">Cancelled</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-6">
          {loading ? (
            <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : filtered.length === 0 ? (
            <div className="doccare-card p-10 text-center text-sm text-slate-500">Nothing here yet.</div>
          ) : (
            <div className="space-y-2">
              {filtered.map((a) => (
                <div key={a.id} className="doccare-card p-4">
                  <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                    <div className="text-center min-w-[64px]">
                      <p className="text-xs text-slate-400">{a.date.slice(5)}</p>
                      <p className="text-lg font-bold text-slate-900">{a.start_time}</p>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900">{a.patient.name}</p>
                      <p className="text-xs text-slate-500 flex items-center gap-2 mt-1">
                        {a.consultation_type === "video" ? <><Video className="w-3 h-3" /> Video</> : <><Building2 className="w-3 h-3" /> In-person</>}
                        <span>· #{a.id.slice(0, 8)}</span>
                      </p>
                    </div>
                    <Badge className="rounded-full self-start sm:self-center">{a.status}</Badge>
                    {tab === "upcoming" && (
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="rounded-full" onClick={() => complete(a.id)} data-testid={`complete-${a.id}`}>Complete</Button>
                        <Button size="sm" variant="outline" className="rounded-full text-red-600 border-red-200" onClick={() => cancel(a.id)} data-testid={`cancel-${a.id}`}>Cancel</Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
